<template>
  <b-modal
    id="modal-configuration-status"
    ref="modalConfigurationStatus"
    size="lg"
    centered
    :title="`Modal ${$t('rfidAndFacerecognition.fieldsHeader.configurationStatus')}`"
  >
    <b-card class="mc-card mc-card__setting mc-card__setting--configuration-status">
      <div class="mb-5">
        <McGrapqlTable
          ref="configurationStatusTable"
          :fields="fields"
          :apiClient="query"
          :searchable="true"
          :hasDelete="false"
          :cardPadding="false"
          :hasCheckbox="false"
          trackBy="id"
          data-me="master-fr-configuration-status-table"
        >
          <template #cell(rfidStatus)="{ value, item }">
            <div class="d-flex align-items-center justify-content-center gap-1">
              <i
                v-if="value.icon && value.status === CONFIGURATION_STATUS_LABEL.VALID"
                class="mc-fa"
                :class="[value.icon, value.variant]"
              ></i>
              <span :class="value.variant">{{ value.text }}</span>
              <i
                v-if="value.icon && value.status === CONFIGURATION_STATUS_LABEL.FAILED"
                class="mc-fa"
                :class="[value.icon, value.variant]"
                @click="retryIdentification(item, 'RFID')"
                style="cursor: pointer"
                :data-me="`master-fr-configuration-status-retry-rfid-${item.id}`"
              ></i>
            </div>
          </template>
          <template #cell(faceStatus)="{ value, item }">
            <div class="d-flex align-items-center justify-content-center gap-1">
              <i
                v-if="value.icon && value.status === CONFIGURATION_STATUS_LABEL.VALID"
                class="mc-fa"
                :class="[value.icon, value.variant]"
              ></i>
              <span :class="value.variant">{{ value.text }}</span>
              <i
                v-if="value.icon && value.status === CONFIGURATION_STATUS_LABEL.FAILED"
                class="mc-fa"
                :class="[value.icon, value.variant]"
                @click="retryIdentification(item, 'FACE')"
                :data-me="`master-fr-configuration-status-retry-face-${item.id}`"
                style="cursor: pointer"
              ></i>
            </div>
          </template>
        </McGrapqlTable>
      </div>
    </b-card>
    <template #modal-footer="{ cancel }">
      <b-row class="mc-modal__filter-footer mx-0">
        <b-col sm="12" md="12" class="mc-modal__filter-footer-btn mt-0">
          <McButton
            :data-me="`master-fr-btn-close-modal-preview-${driverId}`"
            variant="outline-primary"
            class="me-2"
            :text="$t('global.action.close')"
            @click="cancel()"
          />
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import McGrapqlTable from '@/components/McGrapqlTable'
import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'
import {
  GET_VEHICLES_BY_FEATURE_SUPPORT,
  RETRY_INDENTIFICATION,
} from '@/services/api/master/rfid-facerecognition'
import { FACE_RECOGNITION_STATUS, RFID_STATUS } from '@/helpers/face-recognition'
const FEATURE_IDENTIFICATION = 'IDENTIFICATION'
const CONFIGURATION_STATUS_LABEL = Object.freeze({
  FAILED: 'FAILED',
  VALID: 'VALID',
  PENDING: 'PENDING',
  EMPTY: 'EMPTY',
})

export default {
  name: 'ModalConfigurationStatus',
  components: {
    McGrapqlTable,
    McButton,
  },
  mixins: [Helper],
  data() {
    return {
      driverId: null,
      CONFIGURATION_STATUS_LABEL,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    fields() {
      return [
        {
          key: 'licensePlate',
          label: this.$t('global.object.licensePlate'),
          isSortable: false,
          formatter: (item) => item || '-',
        },
        {
          key: 'faceStatus',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.faceRecognition'),
          isSortable: false,
          formatter: (item) => this.formatLabelStatus(item) || '-',
          customColumn: true,
          thClass: 'mc-table--150',
        },
        {
          key: 'rfidStatus',
          label: this.$t('rfidAndFacerecognition.fieldsHeader.rfId'),
          isSortable: false,
          formatter: (item) => this.formatLabelStatus(item) || '-',
          customColumn: true,
          thClass: 'mc-table--150',
        },
        {
          key: 'vehicle2Vgroup',
          label: this.$t('global.object.fleetGroup'),
          isSortable: false,
          formatter: (items) => (items ? items.map((item) => item.vgroup.name).join(', ') : '-'),
        },
        {
          key: 'vehicleType',
          label: this.$t('global.field.vehicleType'),
          isSortable: false,
          formatter: (item) => (item ? `${item.manufacturer} / ${item.model} / ${item.type}` : '-'),
        },
      ]
    },
    query() {
      return {
        get: async (opts) => {
          const res = await this.$apollo.query({
            query: GET_VEHICLES_BY_FEATURE_SUPPORT,
            variables: {
              filter: {
                orderBy: opts.orderBy,
                search: opts.search,
                pagination: {
                  page: opts.pagination.page,
                  show: opts.pagination.show,
                },
                feature: FEATURE_IDENTIFICATION,
                driverIds: [this.driverId],
              },
            },
            fetchPolicy: 'no-cache',
          })
          const data = res?.data?.vehiclesByFeatureSupport.vehiclesByFeatureSupport

          return {
            ...data,
            data: data.data.map((vehicle) => ({
              ...vehicle,
              rfidStatus: this.getRfidStatus(vehicle.identification),
              faceStatus: this.getFaceRecognitionStatus(vehicle.identification),
            })),
          }
        },
      }
    },
  },

  methods: {
    show(driverId) {
      this.driverId = driverId
      this.$bvModal.show('modal-configuration-status')
    },
    hide() {
      this.$bvModal.hide('modal-configuration-status')
    },
    getRfidStatus(identification) {
      if (!identification || identification.length === 0) return CONFIGURATION_STATUS_LABEL.EMPTY
      const statuses = identification.map((item) => item.rfid?.status)

      if (statuses.some((status) => status === null)) return CONFIGURATION_STATUS_LABEL.EMPTY
      if (statuses.includes(RFID_STATUS.PENDING)) return CONFIGURATION_STATUS_LABEL.PENDING
      if (statuses.every((status) => status === RFID_STATUS.SUCCESS))
        return CONFIGURATION_STATUS_LABEL.VALID

      return CONFIGURATION_STATUS_LABEL.FAILED
    },

    getFaceRecognitionStatus(identification) {
      if (!identification || identification.length === 0) return CONFIGURATION_STATUS_LABEL.EMPTY
      const statuses = identification.map((item) => item.face?.status)

      if (statuses.some((status) => status === null)) return CONFIGURATION_STATUS_LABEL.EMPTY

      if (statuses.some((status) => status === FACE_RECOGNITION_STATUS.INVALID))
        return CONFIGURATION_STATUS_LABEL.FAILED

      if (statuses.includes(FACE_RECOGNITION_STATUS.PROCESS))
        return CONFIGURATION_STATUS_LABEL.PENDING

      if (statuses.every((status) => status === FACE_RECOGNITION_STATUS.VALID))
        return CONFIGURATION_STATUS_LABEL.VALID

      return CONFIGURATION_STATUS_LABEL.FAILED
    },
    formatLabelStatus(status) {
      const labels = {
        [CONFIGURATION_STATUS_LABEL.PENDING]: this.$t(
          'rfidAndFacerecognition.configurationStatusLabel.processing'
        ),
        [CONFIGURATION_STATUS_LABEL.FAILED]: this.$t(
          'rfidAndFacerecognition.configurationStatusLabel.failed'
        ),
        [CONFIGURATION_STATUS_LABEL.VALID]: this.$t(
          'rfidAndFacerecognition.configurationStatusLabel.applied'
        ),
        [CONFIGURATION_STATUS_LABEL.EMPTY]: '-',
      }
      const variant = {
        [CONFIGURATION_STATUS_LABEL.PENDING]: 'text-secondary',
        [CONFIGURATION_STATUS_LABEL.FAILED]: 'text-danger',
        [CONFIGURATION_STATUS_LABEL.VALID]: 'text-success',
        [CONFIGURATION_STATUS_LABEL.EMPTY]: 'text-muted',
      }
      const icon = {
        [CONFIGURATION_STATUS_LABEL.PENDING]: '',
        [CONFIGURATION_STATUS_LABEL.FAILED]: 'icon-outline_refresh',
        [CONFIGURATION_STATUS_LABEL.VALID]: 'icon-fill_checkmark-circle',
        [CONFIGURATION_STATUS_LABEL.EMPTY]: '',
      }

      return {
        text: labels[status] ?? this.$t('rfidAndFacerecognition.configurationStatusLabel.failed'),
        icon: icon[status] ?? '',
        variant: variant[status] ?? 'text-danger',
        status: status ?? CONFIGURATION_STATUS_LABEL.FAILED,
      }
    },
    async retryIdentification(item, type) {
      try {
        const vehicleIds = [item.id]

        const driverIds = (item.identification ?? [])
          .map((driver) => driver?.driverId)
          .filter(Boolean)

        const res = await this.$apollo.mutate({
          mutation: RETRY_INDENTIFICATION,
          variables: {
            input: {
              vehicleIds: vehicleIds,
              driverIds: driverIds,
              methods: type,
            },
          },
        })

        const { isSuccessful, message } = res.data.retryIdentification

        if (!isSuccessful) {
          this.$mcToast.error(message || this.$t('global.alert.text.failed'))
          return false
        }

        this.$mcToast.success(message)
        if (this.$refs.configurationStatusTable) {
          setTimeout(() => {
            this.$refs.configurationStatusTable.getData({
              limit: this.$refs.configurationStatusTable.limit,
              page: 1,
            })
          }, 500)
        }
      } catch (error) {
        console.error('Failed to retry identification', error)
        this.$mcToast.error(this.$t('global.alert.text.failed'))
      }
    },
  },
}
</script>
<style scoped></style>
